import PageHeader from "../Components/PageHeader";
import Section from "../Components/Section";
import Card from "../Components/Card";
import { FaRegLightbulb, FaRegHandshake } from "react-icons/fa6";
import { LuSettings } from "react-icons/lu";
import { FaPeopleGroup } from "react-icons/fa6";
import useChangeTitle from "../Hooks/useChangeTitle.jsx";

export default function Decoration() {
  useChangeTitle("Decoration Services");

  const decorationFeatures = [
    {
      index: "1",
      title: "Custom Design",
      desc: "Unique interior designs tailored to reflect your personal style and functional requirements.",
      icon: <FaRegLightbulb />,
    },
    {
      index: "2",
      title: "Quality Materials",
      desc: "Premium finishing materials and fixtures from trusted suppliers ensuring durability and aesthetics.",
      icon: <FaRegHandshake />,
    },
    {
      index: "3",
      title: "Expert Craftsmanship",
      desc: "Skilled craftsmen and contractors delivering precise installation and superior finishing work.",
      icon: <LuSettings />,
    },
    {
      index: "4",
      title: "Project Management",
      desc: "Complete project coordination from design concept to final installation with timeline adherence.",
      icon: <FaPeopleGroup />,
    },
  ];

  return (
    <>
      <PageHeader heading="Interior Decoration" />
      
      {/* Hero Section with Luxury Theme */}
      <div className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/services/decoration.jpg" 
            alt="Interior Decoration" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/90 via-orange-800/80 to-amber-900/90"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-6xl mx-auto">
          <div className="animate-fade-in">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight">
              Luxury Interior
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">
                Design Excellence
              </span>
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-amber-100 max-w-4xl mx-auto leading-relaxed">
              Creating sophisticated spaces that blend timeless elegance with contemporary functionality, 
              transforming your vision into exceptional interior masterpieces.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white px-10 py-4 rounded-full font-semibold text-lg transform hover:scale-105 transition-all duration-300 shadow-2xl">
                Design Consultation
              </button>
              <button className="border-2 border-white text-white hover:bg-white hover:text-amber-800 px-10 py-4 rounded-full font-semibold text-lg transform hover:scale-105 transition-all duration-300">
                View Portfolio
              </button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent"></div>
      </div>

      {/* Services Overview with Luxury Design */}
      <Section 
        title="Our Decoration Services" 
        desc="Exquisite interior solutions crafted with attention to detail and luxury materials"
        className="py-24 bg-gradient-to-br from-white via-amber-50 to-white relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-32 h-32 bg-amber-100 rounded-full -translate-x-16 -translate-y-16 opacity-50"></div>
        <div className="absolute bottom-0 right-0 w-48 h-48 bg-orange-200 rounded-full translate-x-24 translate-y-24 opacity-30"></div>
        
        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mt-12">
          {[
            { 
              title: "Interior Design", 
              desc: "Complete interior design solutions from concept development to final execution with 3D visualization.",
              icon: "🎨",
              color: "from-amber-500 to-orange-500"
            },
            { 
              title: "Wall Finishes", 
              desc: "Premium wall treatments including paint, wallpaper, wood paneling, and decorative plaster finishes.",
              icon: "🖼️",
              color: "from-orange-500 to-red-500"
            },
            { 
              title: "Flooring Solutions", 
              desc: "Wide range of flooring options including marble, tiles, hardwood, and luxury vinyl installations.",
              icon: "🏛️",
              color: "from-brown-500 to-amber-500"
            },
            { 
              title: "Ceiling Design", 
              desc: "Custom ceiling designs with gypsum board, coffered ceilings, and integrated lighting solutions.",
              icon: "✨",
              color: "from-yellow-500 to-amber-500"
            },
            { 
              title: "Custom Millwork", 
              desc: "Bespoke carpentry including built-in cabinets, wardrobes, and decorative architectural elements.",
              icon: "🪚",
              color: "from-brown-600 to-orange-600"
            },
            { 
              title: "Lighting Design", 
              desc: "Decorative lighting schemes with chandeliers, accent lighting, and mood lighting systems.",
              icon: "💡",
              color: "from-amber-400 to-yellow-500"
            }
          ].map((service, index) => (
            <div key={index} className="group relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-600 rounded-2xl blur opacity-0 group-hover:opacity-25 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100 overflow-hidden">
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${service.color}`}></div>
                <div className="p-8">
                  <div className="flex items-center mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-r ${service.color} rounded-lg flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300`}>
                      <span className="text-white text-xl">{service.icon}</span>
                    </div>
                    <h3 className="font-bold text-xl text-gray-800 group-hover:text-amber-600 transition-colors">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">{service.desc}</p>
                </div>
                <div className="absolute bottom-0 right-0 w-20 h-20 bg-amber-50 rounded-tl-full opacity-50"></div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Project Types with Luxury Showcase */}
      <div className="py-24 bg-gradient-to-br from-gray-900 via-amber-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-32 h-32 border border-amber-400 rounded-full opacity-20"></div>
          <div className="absolute bottom-20 right-20 w-24 h-24 border border-orange-400 rounded-full opacity-30"></div>
          <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-amber-500 rounded-full opacity-10"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Project Applications</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We deliver exceptional decoration services across various sectors, creating bespoke environments 
              that reflect sophistication and functionality.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { 
                icon: "🏢", 
                title: "Commercial Offices", 
                desc: "Professional workspace design for productivity and brand representation",
                color: "from-amber-500 to-orange-500"
              },
              { 
                icon: "☕", 
                title: "Restaurants & Cafes", 
                desc: "Ambiance creation for dining establishments and hospitality venues",
                color: "from-orange-500 to-red-500"
              },
              { 
                icon: "🏪", 
                title: "Retail Spaces", 
                desc: "Attractive store designs that enhance customer experience and sales",
                color: "from-red-500 to-pink-500"
              },
              { 
                icon: "🏠", 
                title: "Residential", 
                desc: "Luxury home interiors reflecting personal style and comfort",
                color: "from-amber-600 to-yellow-500"
              }
            ].map((type, index) => (
              <div key={index} className="text-center group">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-500 transform hover:scale-105">
                  <div className={`w-16 h-16 bg-gradient-to-br ${type.color} rounded-full flex items-center justify-center mx-auto mb-6 group-hover:rotate-12 transition-transform duration-300`}>
                    <span className="text-2xl">{type.icon}</span>
                  </div>
                  <h3 className="font-bold text-lg mb-4 text-white group-hover:text-amber-300 transition-colors">
                    {type.title}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{type.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <Section 
        title="Why Choose Our Decoration Services" 
        className="py-16 bg-white"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {decorationFeatures.map((feature) => (
            <Card
              key={feature.index}
              index={feature.index}
              icon={feature.icon}
              title={feature.title}
              desc={feature.desc}
            />
          ))}
        </div>
      </Section>

      {/* Project Gallery with Elegant Portfolio Design */}
      <Section 
        title="Our Decoration Projects" 
        desc="A showcase of our finest interior design achievements and luxury transformations"
        className="py-24 bg-gradient-to-br from-white via-amber-50 to-white relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-32 h-32 bg-amber-100 rounded-full -translate-x-16 -translate-y-16 opacity-50"></div>
        <div className="absolute bottom-0 right-0 w-48 h-48 bg-orange-200 rounded-full translate-x-24 translate-y-24 opacity-30"></div>
        
        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mt-12">
          {[
            {
              image: "/images/home/decor home.jpg",
              title: "Trigger Cafe",
              desc: "River Walk Mall - Complete Interior",
              gradient: "from-amber-500/80 to-orange-600/80"
            },
            {
              image: "/images/services/decoration.jpg",
              title: "Commercial Spaces",
              desc: "Professional Interior Design",
              gradient: "from-orange-500/80 to-red-600/80"
            },
            {
              image: "/images/home/c2.jpg",
              title: "Luxury Projects",
              desc: "High-End Interior Finishing",
              gradient: "from-amber-600/80 to-yellow-600/80"
            }
          ].map((project, index) => (
            <div key={index} className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src={project.image} 
                  alt={project.title} 
                  className="w-full h-80 object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${project.gradient} opacity-0 group-hover:opacity-90 transition-opacity duration-500`}></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                  <h4 className="text-2xl font-bold mb-2 group-hover:text-amber-300 transition-colors">
                    {project.title}
                  </h4>
                  <p className="text-gray-200 mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-200">
                    {project.desc}
                  </p>
                  <div className="w-16 h-1 bg-gradient-to-r from-amber-400 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-300"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Call to Action for Portfolio */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white p-8 rounded-2xl max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">Ready to Transform Your Space?</h3>
            <p className="text-amber-100 mb-6">
              Let our expert designers create a bespoke interior that reflects your vision and exceeds your expectations.
            </p>
            <button className="bg-white text-amber-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors transform hover:scale-105 duration-300">
              Schedule Design Consultation
            </button>
          </div>
        </div>
      </Section>
    </>
  );
} 